// The drain's mark arithmetic — spec §3.2.
//
// Kept out of the component and named, because it is the one thing on this panel
// that has to be RIGHT rather than merely look right, and because the
// high-volume branch is invisible until a property crosses the threshold.
//
// NOT A VIOLATION OF "render, don't compute". Nothing here derives a METRIC:
// `count` and `dropped_here` are rendered exactly as the RPC gives them. What is
// computed is how many little squares to draw for a number the backend already
// decided — a presentation detail, the same kind of arithmetic as choosing a bar
// width. No figure produced here is ever shown as a statistic.

// Above roughly 400 conversations one mark per conversation stops being legible
// — at Verde's 110 it is fine, at 2,000 it is a grey smear and the browser is
// laying out 12,000 nodes. The spec's instruction at that point is explicit:
// each mark represents a fixed number and the row says the ratio quietly. It
// also says, in bold, do not switch to a bar.
export const MARK_THRESHOLD = 400;

// The per-mark scale, chosen so the row stays under the threshold. Round numbers
// only: "each mark is 5 conversations" is a sentence a reader can hold, "each
// mark is 3.7" is not.
const SCALES = [1, 2, 5, 10, 25, 50, 100, 250, 500, 1000];

/**
 * How many conversations one mark stands for, given the widest row.
 * @param {number} total the largest stage count on the page — the first row,
 *   normally, and the one every other row is drawn against.
 */
export const markScale = (total) => {
  const n = Number(total);
  if (!Number.isFinite(n) || n <= 0) return 1;
  if (n <= MARK_THRESHOLD) return 1;
  return SCALES.find((s) => n / s <= MARK_THRESHOLD) || SCALES[SCALES.length - 1];
};

/**
 * The marks for one row: a constant-length row, with the ones that made it lit.
 *
 * THE ROW LENGTH IS CONSTANT AND THAT IS THE WHOLE POINT — every row draws
 * `Math.ceil(total / scale)` marks, and only how many are LIT changes. A row
 * that drew fewer marks would be a bar chart with square corners.
 *
 * @returns {{total: number, lit: number, scale: number}} counts of MARKS, not of
 *   conversations.
 */
export const rowMarks = (count, total, scale) => {
  const s = scale > 0 ? scale : 1;
  const totalMarks = Math.max(0, Math.ceil(Number(total || 0) / s));
  const rawLit = Math.round(Number(count || 0) / s);
  // A stage with any survivors at all keeps at least one lit mark: rounding a
  // real 3-of-2000 down to zero would draw an empty row for a stage that is not
  // empty, which is a lie the eye reads before any label corrects it.
  const lit = Number(count) > 0 ? Math.max(1, Math.min(totalMarks, rawLit)) : 0;
  return { total: totalMarks, lit, scale: s };
};

/**
 * The gaps between rows: what was lost, in the backend's words.
 *
 * Returns one entry per PAIR of adjacent stages, so the caller can render it in
 * the space between them rather than in a list underneath.
 *
 * ZERO IS RENDERED, ABSENT IS NOT — and the two are different on this payload.
 * Voice's Quoted→Captured step really is 0 today, and that is a fact about the
 * funnel worth seeing: it says nobody was lost there. A NULL `dropped_here`
 * means the backend did not report a loss for that step, and inventing "−0" for
 * it would state something the data does not.
 */
export const rowGaps = (stages) => {
  const list = Array.isArray(stages) ? stages : [];
  const gaps = [];
  for (let i = 1; i < list.length; i += 1) {
    const stage = list[i];
    const dropped = stage?.dropped_here;
    if (dropped == null) continue;
    gaps.push({
      afterIndex: i - 1,
      key: stage.key,
      dropped: Number(dropped),
      // The backend's own fragment. Never one we write: a loss with no label
      // renders the number alone rather than a sentence we invented for it.
      label: stage.drop_label || null,
    });
  }
  return gaps;
};

/**
 * The single largest loss on the page, which the spec gives stronger treatment.
 * Null when nothing was lost anywhere — nothing to emphasise.
 */
export const largestGap = (gaps) => {
  let worst = null;
  for (const g of gaps || []) {
    if (g.dropped <= 0) continue;
    if (!worst || g.dropped > worst.dropped) worst = g;
  }
  return worst;
};
